/**
 * Org Y.Doc Store
 *
 * WS-only connection (no IndexedDB — the org room is re-seeded from MongoDB
 * on every cold start, which is incompatible with safe IDB caching).
 *
 * Room name: org:{dataspaceId}
 *
 * Y.Doc structure:
 *   Y.Map('edges') — edgeId → JSON { parentId, childId, pos, expanded, kind }
 *   Y.Map('nodes') — entityId → JSON { name, icon, entityType }
 *
 * Observers push changes into dataspaceTreeStore and the entity registry
 * (spaceEntityStore + globalEntityStore) so the sidebar updates in real time
 * when the backend (or another client) mutates tree structure or renames
 * entities.
 *
 * ⚠️  Snapshot ceiling: The websocket server enforces PAYLOAD_BUDGET_BYTES['org:']
 *     (default 16 MiB) for the initial full-room snapshot. Normal orgs are well
 *     under this limit. If a very large org triggers snapshot truncation the
 *     global tree will be partially visible on cold start (subsequent CRDT ops
 *     will fill it in). A follow-up can add lazy subtree edge sync using the
 *     REST GET /api/dataspaces/{id}/edges?parent_id=<id> filter to stream
 *     deeper subtrees on demand rather than front-loading them.
 */

import * as Y from 'yjs'
import type { WebsocketProvider } from 'y-websocket'
import fetchService from '../services/fetchService'
import { MAIL_CRDT_TYPES } from './mailCrdtBridge'
import { entityRegistry } from './entityRegistry'
import { API_BASE_URL, WEBSOCKET_URL } from '../api/config'
import { wsClient, WSClientDocMismatchError, type WSClientHandle } from '../services/yjs/wsClientSingleton'
import { createOverflowDetector } from '../services/yjs/wsOverflowRecovery'
import { _DEFAULT_HARD_LIMITS_BY_PREFIX } from '../services/yjs/wsSizeBudget'
import { createFileLogger } from '../utils/logger'
import useUserStore from './userStore'

const log = createFileLogger('ORG_YDOC_STORE', { enabled: false })

interface OrgYjsDebugState {
  dataspaceId: string | null
  wsConnected: boolean
  wsSynced: boolean
  wsUrl: string | null
  edgeCount: number
  nodeCount: number
  lastUpdateAt: number | null
}

let _debugState: OrgYjsDebugState = {
  dataspaceId: null,
  wsConnected: false,
  wsSynced: false,
  wsUrl: null,
  edgeCount: 0,
  nodeCount: 0,
  lastUpdateAt: null,
}

export function getOrgYDocDebugState(): OrgYjsDebugState {
  return { ..._debugState }
}

// ── Connection status (for a lightweight UI indicator) ───────────────────────
// Mirrors y-websocket's own status values so callers don't need a translation
// layer. 'disconnected' covers both "never connected yet" and "was connected,
// dropped" — the UI indicator only needs to know "can I trust what I'm
// looking at right now", not the full history.
export type OrgConnectionStatus = 'connecting' | 'connected' | 'disconnected'

let _connectionStatus: OrgConnectionStatus = 'disconnected'
const _connectionStatusListeners = new Set<() => void>()

function _setConnectionStatus(status: OrgConnectionStatus): void {
  if (_connectionStatus === status) return
  _connectionStatus = status
  _connectionStatusListeners.forEach(fn => fn())
}

/** Current org-room WS connection status. Safe to call at any time. */
export function getOrgConnectionStatus(): OrgConnectionStatus {
  return _connectionStatus
}

/** Subscribe to org-room WS connection status changes. Returns an unsubscribe function. */
export function subscribeToOrgConnectionStatus(cb: () => void): () => void {
  _connectionStatusListeners.add(cb)
  return () => _connectionStatusListeners.delete(cb)
}

// ── Synced dimension (for the global connection indicator) ──────────────────
// `_connectionStatus === 'connected'` only means the socket opened — a
// "connected zombie" (see wsReconnectCoordinator's zombie detection) reports
// exactly that while never completing the Y.js sync handshake. A UI indicator
// that only looks at connection status can say "connected" for a room that's
// actually delivering nothing. This tracks the Hocuspocus 'sync' event
// separately so callers can require BOTH dimensions.
let _connectionSynced = false
const _connectionSyncedListeners = new Set<() => void>()

function _setConnectionSynced(synced: boolean): void {
  if (_connectionSynced === synced) return
  _connectionSynced = synced
  _connectionSyncedListeners.forEach(fn => fn())
}

/** Whether the org room has completed at least one sync since its current connection attempt. */
export function getOrgConnectionSynced(): boolean {
  return _connectionSynced
}

/** Subscribe to org-room synced-state changes. Returns an unsubscribe function. */
export function subscribeToOrgConnectionSynced(cb: () => void): () => void {
  _connectionSyncedListeners.add(cb)
  return () => _connectionSyncedListeners.delete(cb)
}

let _ydoc: Y.Doc | null = null
let _ws: WebsocketProvider | null = null
let _wsHandle: WSClientHandle | null = null
let _currentDataspaceId: string | null = null
let _readyResolvers: Array<{ resolve: () => void; reject: (err: Error) => void }> = []
let _isReady = false

type EntityChangeCallback = (action: 'add' | 'update' | 'delete', entityId: string, data?: any) => void
const _entityListeners = new Set<EntityChangeCallback>()

type ReconnectCallback = () => void
const _reconnectListeners = new Set<ReconnectCallback>()

export function subscribeToOrgReconnect(cb: ReconnectCallback): () => void {
  _reconnectListeners.add(cb)
  return () => _reconnectListeners.delete(cb)
}


function _notifyReadyWaiters(): void {
  _isReady = true
  const waiters = _readyResolvers.splice(0)
  waiters.forEach(({ resolve }) => resolve())
}

export function initOrgYDocStore(dataspaceId: string): void {
  if (!dataspaceId) return

  if (_currentDataspaceId === dataspaceId && _ws) {
    console.log(`[OrgYDoc] Already connected for dataspace ...${dataspaceId.slice(-6)}`)
    return
  }

  destroyOrgYDocStore()

  console.log(`[OrgYDoc] Initializing for dataspace ...${dataspaceId.slice(-6)}`)

  _currentDataspaceId = dataspaceId
  _isReady = false
  _debugState = {
    dataspaceId,
    wsConnected: false,
    wsSynced: false,
    wsUrl: WEBSOCKET_URL,
    edgeCount: 0,
    nodeCount: 0,
    lastUpdateAt: null,
  }

  _ydoc = new Y.Doc({ guid: `org:${dataspaceId}` })
  const edgesMap = _ydoc.getMap<string>('edges')
  const nodesMap = _ydoc.getMap<string>('nodes')
  const pendingCmdsMap = _ydoc.getMap<string>('pendingCmds')

  const roomName = `org:${dataspaceId}`

  // Evict any stale WSClient entry for this room. This handles the Vite HMR
  // case: the wsClient singleton lives on globalThis and survives module
  // re-execution, but the module-level _wsHandle is reset to null on reload.
  // destroyOrgYDocStore() had no handle to close, so the old entry (with the
  // old Y.Doc) is still registered. Without eviction the open() below would
  // throw WSClientDocMismatchError and leave the store half-initialized,
  // causing a persistent crash loop on every subsequent render.
  wsClient.evictStaleEntry(roomName, _ydoc)

  // resyncInterval is a no-op here: the transport is HocuspocusWebsocketProvider
  // (not raw y-websocket), which accepts this option only for signature parity
  // and manages resync internally — it never schedules the periodic full-state
  // re-upload y-websocket used to. Passed through anyway for clarity/parity with
  // other WSClient.open() call sites.
  try {
    _wsHandle = wsClient.open({
      roomname: roomName,
      doc: _ydoc,
      connect: true,
      resyncInterval: -1,
      maxBackoffTime: 30_000,
    })
  } catch (err) {
    // Clean up the Y.Doc we just created so the store is not left in a
    // half-initialized state (_currentDataspaceId set but no provider). The
    // eviction above makes WSClientDocMismatchError effectively unreachable;
    // any throw here is an unexpected provider-construction failure, which we
    // surface (re-throw) rather than mask — but only after restoring a clean,
    // uninitialized state so the next render can retry from scratch.
    _ydoc.destroy()
    _ydoc = null
    _currentDataspaceId = null
    _isReady = false
    _debugState = { dataspaceId: null, wsConnected: false, wsSynced: false, wsUrl: null, edgeCount: 0, nodeCount: 0, lastUpdateAt: null }
    if (err instanceof WSClientDocMismatchError) {
      console.error('[OrgYDoc] WSClientDocMismatchError after eviction — should not happen', err)
    }
    throw err
  }
  _ws = _wsHandle.provider

  // ── Idle-connection keepalive ────────────────────────────────────────────────
  // The org room carries no cursors/presence, so nothing ever calls
  // awareness.setLocalState() — meaning y-protocols' own awareness self-refresh
  // heartbeat (which re-sends local state every ~15s to keep it from going
  // stale) never activates, and no other periodic message flows in either
  // direction while the tree is quiet. Hocuspocus's server-side connection
  // watchdog (`timeout: 30_000` in server.js) closes any connection that goes
  // 30s without receiving an application message from the client — with no
  // heartbeat, that fires on essentially every idle org-room connection,
  // producing constant ~30-60s connect/disconnect churn even while the tab is
  // foregrounded (confirmed in prod CloudWatch: connect→disconnect deltas
  // consistently land at 29-60s for otherwise-healthy sessions). Setting any
  // non-null local awareness state arms that self-refresh timer, giving the
  // connection a small periodic heartbeat in both directions and keeping it
  // comfortably inside the server's 30s window indefinitely.
  _wsHandle.awareness.setLocalState({})

  // ── Overflow circuit-breaker detection ──────────────────────────────────────
  // See wsOverflowRecovery.ts for the detection heuristic (size-gated, shared
  // with conversationYjsStore). Recovery itself — destroy + re-init the local
  // Y.Doc rather than reload the page — stays here because it's store-specific:
  // a page reload loops if the server's circuit is still open (HTTP 503 → 1006
  // → reload → ∞), while a targeted reset produces an empty Y.Doc which cannot
  // overflow, letting the provider back off until the server-side circuit
  // window expires.
  const _capturedDataspaceId = dataspaceId
  const _overflowDetector = createOverflowDetector(_ws, {
    // Half of the room's own hard payload budget (see wsSizeBudget) — a doc
    // this far under budget cannot be the overflow the breaker exists to catch.
    sizeThresholdBytes: (_DEFAULT_HARD_LIMITS_BY_PREFIX['org:'] ?? 16 * 1024 * 1024) / 2,
    getDocBytes: () => (_ydoc ? Y.encodeStateAsUpdateV2(_ydoc).byteLength : 0),
    onOverflow: () => {
      console.error(
        `[OrgYDoc] ⚠️ Payload overflow detected for ...${_capturedDataspaceId.slice(-6)} ` +
        `— destroying local Y.Doc and reconnecting fresh in 5s`
      )
      destroyOrgYDocStore()
      setTimeout(() => {
        if (_currentDataspaceId !== null && _currentDataspaceId !== _capturedDataspaceId) return
        initOrgYDocStore(_capturedDataspaceId)
      }, 5000)
    },
  })

  _setConnectionStatus('connecting')

  let _prevWsStatus: string | null = null
  _ws.on('status', ({ status }: { status: string }) => {
    _debugState.wsConnected = status === 'connected'
    console.log(`[OrgYDoc] WS ${status} for dataspace ...${dataspaceId.slice(-6)}`)
    if (status === 'connecting' || status === 'connected' || status === 'disconnected') {
      _setConnectionStatus(status)
    }
    // Any transition away from a live, synced socket invalidates the last sync —
    // a fresh connect() always needs its own 'sync' event before data can be trusted.
    if (status !== 'connected') _setConnectionSynced(false)
    // Re-attach the close listener whenever y-websocket creates a new socket
    // (the underlying ws is recreated on every reconnect).
    if (status === 'connecting') _overflowDetector.attachToCurrentSocket()
    if (status === 'connected' && _prevWsStatus !== null && _prevWsStatus !== 'connected') {
      console.log(`[OrgYDoc] WS reconnected — notifying ${_reconnectListeners.size} listeners`)
      _reconnectListeners.forEach(fn => fn())
    }
    _prevWsStatus = status
  })

  _ws.on('sync', (synced: boolean) => {
    if (!synced) return
    _setConnectionSynced(true)
    _debugState.wsSynced = true
    _debugState.edgeCount = edgesMap.size
    _debugState.nodeCount = nodesMap.size
    console.log(`[OrgYDoc] WS synced: ${edgesMap.size} edges, ${nodesMap.size} nodes for ...${dataspaceId.slice(-6)}`)
    // Perf instrumentation — note when the WS sync event arrives
    import('../utils/perfInstrumentation').then(({ perf }) => perf.mark('org_ydoc_sync')).catch(() => {})

    // Unblock waiters as soon as WS sync arrives, before the full tree apply.
    // On tiny org docs (new-user signup: 3 edges / ~13b) waiters were previously
    // blocked for an extra macrotask + _applyFullTreeSync + _applyFullNodeSync
    // (~1-2s). Unblocking early lets spaceEntityStore Stage 2 proceed immediately
    // while the tree/node sync runs async in the background.
    _notifyReadyWaiters()

    // Defer the heavy tree/node sync off the Yjs message handler via setTimeout so the
    // browser can service other events (input, paint) instead of blocking for ~1.7s.
    // Capture the dataspaceId at closure time so we can bail out if the store was
    // destroyed and re-initialized for a different dataspace before the timeout fires.
    const syncedDataspaceId = dataspaceId
    setTimeout(() => {
      if (_currentDataspaceId !== syncedDataspaceId) {
        console.log(`[OrgYDoc] Deferred sync for ...${syncedDataspaceId.slice(-6)} discarded — dataspace changed`)
        return
      }
      Promise.all([
        _applyFullTreeSync(edgesMap),
        _applyFullNodeSync(nodesMap),
      ]).catch((err) => {
        console.warn('[OrgYDoc] Full sync apply failed:', err)
      })
    }, 0)
  })

  edgesMap.observe((event: Y.YMapEvent<string>) => {
    if (!_debugState.wsSynced) return
    _debugState.edgeCount = edgesMap.size
    _debugState.lastUpdateAt = Date.now()
    _handleEdgeChanges(event, edgesMap)
  })

  nodesMap.observe((event: Y.YMapEvent<string>) => {
    if (!_debugState.wsSynced) return
    _debugState.nodeCount = nodesMap.size
    _debugState.lastUpdateAt = Date.now()
    _handleNodeChanges(event)
  })

  // Track command IDs already executed by this tab so that CRDT propagation delays
  // between tabs don't cause both tabs to execute the same non-idempotent command.
  const _executedCmdIds = new Set<string>()

  pendingCmdsMap.observe((event: Y.YMapEvent<string>) => {
    const now = Date.now()
    const toDelete: string[] = []
    // Snapshot the current user id once per observer batch. The per-cmd
    // targetUserId gate (below) needs this synchronously before deciding
    // whether to schedule the cmd for deletion or mark it as executed.
    const _myUserId = useUserStore.getState().auth0Id ?? null
    event.changes.keys.forEach((change, id) => {
      if (change.action !== 'add') return
      // De-duplicate: skip if this tab already executed this command.
      if (_executedCmdIds.has(id)) return
      const raw = pendingCmdsMap.get(id)
      if (!raw) {
        // Empty entry — clean it up regardless of target.
        toDelete.push(id)
        return
      }
      let parsed: any
      try {
        parsed = JSON.parse(raw)
      } catch {
        // Malformed JSON — clean up so it doesn't accumulate.
        toDelete.push(id)
        return
      }
      try {
        const { name, params, ts, targetUserId } = parsed
        // Per-user routing: cmds stamped with targetUserId only execute on that
        // user's tabs. Without this gate, every member of the org room would
        // execute UI commands like space_open_window — a teammate would see
        // your AI-created doc pop open in their UI. Tabs that are NOT the
        // target leave the cmd in place so the targeted user's tab can still
        // pick it up; the target's tab is responsible for cleanup. If no
        // target's tab is online the cmd ages out via the staleness window
        // below (and pendingCmds isn't persisted, so it dies with the room).
        if (targetUserId) {
          if (!_myUserId || _myUserId !== targetUserId) return
        }
        // Schedule deletion outside this observer to avoid re-entrant transactions.
        toDelete.push(id)
        // Staleness window: tabs that reconnect after a short disconnect
        // Generic commands time out at 5s. Video studio broadcasts get 60s
        // because render jobs are long and a tab opened mid-run should still
        // receive the completed result.
        // Note: image_studio_result/progress are no longer broadcast — Image
        // Studio now writes results directly into the imageproject CRDT room.
        const STALE_MS =
          name === 'video_studio_result' || name === 'video_studio_progress'
            ? 60_000
            : 5_000
        if (now - ts > STALE_MS) return  // stale — dropped
        _executedCmdIds.add(id)
        // Evict oldest half instead of clearing entirely — a full clear would forget
        // recently-executed IDs and allow re-execution of replayed CRDT events.
        if (_executedCmdIds.size > 500) {
          const toEvict = [..._executedCmdIds].slice(0, 250)
          toEvict.forEach(evictId => _executedCmdIds.delete(evictId))
        }
        console.log(`[OrgYDoc] pendingCmd: ${name}${params ? ` params=${JSON.stringify(params).slice(0, 160)}` : ''}`)
        if (name === 'video_studio_progress' || name === 'video_studio_result') {
          console.log(`[VS/Generate] [OrgYDoc] received ${name}`, params)
        }

        // Infrastructure resync command — re-seeds the sidebar tree from the
        // org Y.Doc snapshot (with MongoDB fallback). Emitted by bulk ingest
        // after each ~500-message page so the browser sees new threads without
        // receiving per-entity CRDT floods.
        if (name === 'orgTreeResync') {
          const targetId = (params as any)?.dataspaceId || _currentDataspaceId
          if (targetId) {
            import('./dataspaceTreeStore').then(({ useDataspaceTreeStore }) => {
              useDataspaceTreeStore.getState().initialize(targetId)
            }).catch(() => {})
          }
          return
        }

        // The websocket server's mass-delete guard refused to persist a batch
        // of deletes from this (or another) tab. The Y.Map locally has the
        // edges removed (the observer ran before the guard tripped) but Mongo
        // is untouched — so we reload the tree from the authoritative server
        // state and toast the user. Without this surface the user sees their
        // sidebar entries reappear silently on the next page load with no
        // explanation.
        if (name === 'org_mass_delete_refused') {
          const p = params as any
          const targetId = p?.dataspaceId || _currentDataspaceId
          Promise.all([
            import('./dataspaceTreeStore'),
            import('../services/ui/toastService'),
          ]).then(([treeMod, toastMod]) => {
            toastMod.toastService.show(
              'Bulk delete refused: the server detected an unusually large deletion (likely a sync glitch) and reverted it. Try again on a smaller selection.',
              'warning',
              { duration: 8000 },
            )
            if (targetId) {
              treeMod.useDataspaceTreeStore.getState().initialize(targetId)
            }
          }).catch(() => {})
          return
        }

        if (name === 'org_membership_revoked') {
          const p = params as any
          Promise.all([
            import('./userStore' as string),
            import('./dataspaceContextStore'),
            import('../services/ui/toastService'),
            import('../services/spaceHelpers'),
          ]).then(async ([userStoreMod, dsMod, toastMod, spaceHelpersMod]) => {
            const myUserId = (userStoreMod.default?.getState?.() as any)?.auth0Id
            if (!myUserId || myUserId !== p?.user_id) return
            const orgName = p?.org_name || 'an organization'
            toastMod.toastService.show(`You were removed from ${orgName}`, 'warning', { duration: 6000 })
            await dsMod.useDataspaceContextStore.getState().switchToPersonal()
            try {
              window.location.href = await spaceHelpersMod.resolveLandingDesktopRoute()
            } catch (err) {
              log.error('[org_membership_revoked] Failed to resolve landing Desktop, reloading in place:', err)
              window.location.reload()
            }
          }).catch(() => {})
          return
        }

        if (name === 'org_membership_added') {
          const p = params as any
          Promise.all([
            import('./dataspaceContextStore'),
            import('../services/ui/toastService'),
          ]).then(([dsMod, toastMod]) => {
            const orgName = p?.org_name || 'a new workspace'
            // Reload the org list so the new workspace appears in the sidebar immediately.
            dsMod.useDataspaceContextStore.getState().loadUserOrgs()
            toastMod.toastService.show(
              `You've been added to ${orgName}. Switch to it from the menu.`,
              'success',
              { duration: 8000 },
            )
          }).catch(() => {})
          return
        }

        import('./commandRegistryStore').then(({ useCommandRegistryStore }) => {
          const registry = useCommandRegistryStore.getState()
          // Cross-tab command broadcasts are best-effort: another tab (or device) may be
          // running a newer/older app version that knows about commands this one doesn't.
          // Silently drop unknown commands rather than throwing, which otherwise surfaces
          // as a top-level React error.
          const isKnown = registry.commands.has(name as any) || registry.commandDefinitions.has(name as any)
          if (!isKnown) {
            console.debug(`[orgYDocStore] Ignoring cross-tab command unknown to this build: ${name}`)
            return
          }
          registry.executeCommand(name, params ?? {})
        }).catch(() => {})
      } catch {
        // Unexpected error after parse (e.g. destructure on a non-object) —
        // schedule cleanup so the entry doesn't loop forever. push twice is
        // harmless: pendingCmdsMap.delete(id) is idempotent.
        toDelete.push(id)
      }
    })
    if (toDelete.length > 0) {
      queueMicrotask(() => {
        if (!_ydoc) return
        _ydoc.transact(() => {
          toDelete.forEach(id => pendingCmdsMap.delete(id))
        })
      })
    }
  })
}

/**
 * Force-reconnect the org room's WS provider, bypassing every idempotence
 * guard along the way. Used by the chat-driven self-heal (see
 * wsRecoveryOnToolError.ts): when a space_ui tool reports "No active desktop
 * session" while THIS tab is actively rendering that failure over SSE, the
 * tab is provably alive — the org room's WS layer, which that check actually
 * depends on, is what's wedged. Falls back to a plain `connect()` if the
 * underlying provider doesn't expose `forceReconnect` (defensive; the
 * production provider always does).
 *
 * No-op if the store hasn't been initialized yet (nothing to reconnect).
 */
export function forceReconnectOrgYDoc(): void {
  if (!_ws) return
  const anyWs = _ws as unknown as { forceReconnect?: () => void }
  if (typeof anyWs.forceReconnect === 'function') {
    anyWs.forceReconnect()
  } else {
    _ws.connect()
  }
}

export function destroyOrgYDocStore(): void {
  if (_wsHandle) {
    // wsHandle.close() handles tokenRefresher.unregister, observability dispose
    // and provider.disconnect+destroy with idempotent semantics.
    _wsHandle.close()
    _wsHandle = null
    _ws = null
  }
  if (_ydoc) {
    _ydoc.destroy()
    _ydoc = null
  }
  _currentDataspaceId = null
  _isReady = false
  // Reject pending waiters so they don't block for their full timeout.
  // Callers of waitForOrgSync / waitForEntitySystemReady handle rejection gracefully
  // via .catch(). Using reject (not resolve) prevents callers from incorrectly
  // assuming sync succeeded and proceeding with stale empty state.
  const pendingCount = _readyResolvers.length
  if (pendingCount > 0) {
    console.warn(`[OrgYDoc] Destroyed with ${pendingCount} pending waiters — rejecting them now`)
    const waiters = _readyResolvers.splice(0)
    waiters.forEach(({ reject }) => reject(new Error('[OrgYDoc] Destroyed before sync completed')))
  } else {
    _readyResolvers = []
  }
  _entityListeners.clear()
  if (_pendingFetchTimer !== null) {
    clearTimeout(_pendingFetchTimer)
    _pendingFetchTimer = null
  }
  _pendingFetchIds.splice(0)
  _pendingFetchScheduled = false
  // Clear the micro-batch edge flush state so stale edges from the old dataspace
  // are not applied to the next dataspace's tree store.
  _pendingEdgeAdds.clear()
  _pendingEdgeRemoves.clear()
  _edgeFlushScheduled = false
  // Invalidate the stable nodes snapshot so stale metadata is not served after re-init.
  _lastStableNodesVersion = -1
  _lastStableNodesSnapshot = null
  _clearRestNodesFallback()
  _notifyNodesListeners()
  _debugState = { dataspaceId: null, wsConnected: false, wsSynced: false, wsUrl: null, edgeCount: 0, nodeCount: 0, lastUpdateAt: null }
  _setConnectionStatus('disconnected')
  _setConnectionSynced(false)
  console.log('[OrgYDoc] Destroyed')
  // Perf instrumentation — track mid-load destroy events
  import('../utils/perfInstrumentation').then(({ perf }) => {
    perf.increment('orgYDocDestroyCount')
    perf.mark('org_ydoc_destroyed')
  }).catch(() => {})
}

export function waitForOrgSync(timeoutMs = 10000): Promise<void> {
  if (_isReady) {
    console.log(`[OrgYDoc] waitForOrgSync: already ready (fast path)`)
    return Promise.resolve()
  }
  console.log(`[OrgYDoc] waitForOrgSync: waiting (timeout=${timeoutMs}ms, pending=${_readyResolvers.length})`)
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`[OrgYDoc] WS sync timeout (${timeoutMs}ms)`))
    }, timeoutMs)
    _readyResolvers.push({
      resolve: () => { clearTimeout(timer); console.log('[OrgYDoc] waitForOrgSync: resolved'); resolve() },
      reject: (err) => { clearTimeout(timer); reject(err) },
    })
  })
}

/** Returns true when the org Y.Doc has completed its initial WS sync. */
export function isOrgSynced(): boolean {
  return _isReady
}

export function writePendingCmdToOrgYDoc(name: string, params: Record<string, unknown> = {}): void {
  if (!_ydoc) return
  const pendingCmdsMap = _ydoc.getMap<string>('pendingCmds')
  const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
  _ydoc.transact(() => {
    pendingCmdsMap.set(id, JSON.stringify({ name, params, ts: Date.now() }))
  })
  console.log(`[OrgYDoc] writePendingCmd: ${name}`)
}


export function writeNodeToOrgYDoc(entityId: string, data: {
  name: string
  icon: string | null
  entityType: string
}): void {
  if (!_ydoc) return
  const nodesMap = _ydoc.getMap<string>('nodes')
  _ydoc.transact(() => {
    nodesMap.set(entityId, JSON.stringify(data))
  })
}

/**
 * Delete a node from the local org Y.Doc, creating a Y.js tombstone.
 *
 * The tombstone propagates to the WS server on the next sync and from there
 * to all other connected clients, clearing any stale "running" Job entries that
 * survived reconnects because no delete was broadcast when the job completed.
 *
 * The `nodesMap.has()` guard is intentional: Y.js only creates a meaningful
 * tombstone when the key was previously set in THIS Y.Doc instance. Calling
 * delete on a key that was never set is a silent no-op with no propagation.
 */
export function deleteNodeFromOrgYDoc(entityId: string): void {
  if (!_ydoc) return
  const nodesMap = _ydoc.getMap<string>('nodes')
  if (!nodesMap.has(entityId)) return
  _ydoc.transact(() => {
    nodesMap.delete(entityId)
  })
}

export function subscribeToEntitySystemChanges(callback: EntityChangeCallback): () => void {
  _entityListeners.add(callback)
  return () => { _entityListeners.delete(callback) }
}

// ── Org nodes reactivity ─────────────────────────────────────────────────────
// A simple version counter lets useSyncExternalStore consumers re-render
// whenever the nodes map changes, without copying the entire Map on each tick.

/**
 * Lightweight metadata for every entity in the org Y.Doc nodes map.
 * Sufficient to render a file-tree row without loading the full entity body.
 */
export interface OrgNodeMeta {
  entityId: string
  name: string
  icon?: string
  entityType: string
}

let _nodesVersion = 0
let _lastStableNodesVersion = -1
let _lastStableNodesSnapshot: Map<string, OrgNodeMeta> | null = null
const _nodesVersionListeners = new Set<() => void>()

// ── REST fallback for node metadata ─────────────────────────────────────────
// The org Y.Doc `nodes` map is the live source, but it's WS-only — unlike
// edges (which dataspaceTreeStore falls back to REST for), there was
// previously no fallback if the WS room was slow, disconnected, or
// circuit-broken. That silently starved every consumer of getOrgNodesSnapshot
// (Drive's global "flattened org" view, SpaceSidebarV2, transparentSpaceTree)
// down to whatever the current desktop's cache already had — the failure
// mode that made the Drive root view collapse to 1-2 items for large orgs.
//
// dataspaceTreeStore.initialize() fetches
// GET /api/dataspaces/{id}/edges?include_nodes=true in parallel with the
// Y.Doc wait and calls setRestNodesFallback() with the result regardless of
// which source won the edges race, so this fallback populates independently
// of Y.Doc health.
let _restNodesFallback: Map<string, OrgNodeMeta> | null = null
let _restNodesFallbackDataspaceId: string | null = null

/**
 * Populate the REST fallback nodes map for `dataspaceId`. Ignored if it no
 * longer matches the currently active dataspace (stale response from a
 * dataspace switch that happened mid-flight).
 */
export function setRestNodesFallback(
  dataspaceId: string,
  nodes: Record<string, { name: string; icon?: string | null; entityType: string }>,
): void {
  if (dataspaceId !== _currentDataspaceId) {
    console.log(`[OrgYDoc] Ignoring stale REST nodes fallback for ...${dataspaceId.slice(-6)} (current is ...${(_currentDataspaceId ?? '').slice(-6)})`)
    return
  }
  const map = new Map<string, OrgNodeMeta>()
  for (const [entityId, meta] of Object.entries(nodes)) {
    map.set(entityId, { entityId, name: meta.name || '', icon: meta.icon ?? undefined, entityType: meta.entityType || '' })
  }
  _restNodesFallback = map
  _restNodesFallbackDataspaceId = dataspaceId

  // Seed-regression detector: if the WS room reports itself synced but its
  // live nodes map is empty while REST just returned real data, the org room
  // seed is broken server-side (see websocket-server seedOrgRoom) — surface
  // it loudly rather than silently limping along on the REST fallback forever.
  if (_isReady && _ydoc && map.size > 0 && _ydoc.getMap<string>('nodes').size === 0) {
    console.warn(`[OrgYDoc] ⚠️ Org Y.Doc reports synced with 0 nodes while REST fallback has ${map.size} — org room seed may be broken for ...${dataspaceId.slice(-6)}`)
  }

  console.log(`[OrgYDoc] REST nodes fallback set: ${map.size} nodes for ...${dataspaceId.slice(-6)}`)
  _notifyNodesListeners()
}

function _clearRestNodesFallback(): void {
  _restNodesFallback = null
  _restNodesFallbackDataspaceId = null
}

/**
 * Force a one-off REST refetch of node metadata for `dataspaceId`, bypassing
 * whatever the Y.Doc currently has. Used by consumers (e.g. Drive's global
 * flattened view) as a self-heal when they detect edges exist but node
 * metadata is unexpectedly empty — a state the Y.Doc's own reconnect/sync
 * logic doesn't otherwise correct on its own within a reasonable time.
 */
export async function refreshOrgNodesFallback(dataspaceId: string): Promise<void> {
  try {
    const response: any = await fetchService(`${API_BASE_URL}/dataspaces/${dataspaceId}/edges?include_nodes=true`, { credentials: 'include' })
    if (response && response.nodes) {
      setRestNodesFallback(dataspaceId, response.nodes)
    } else {
      console.warn('[OrgYDoc] refreshOrgNodesFallback: unexpected response shape', response)
    }
  } catch (err) {
    console.warn('[OrgYDoc] refreshOrgNodesFallback failed:', err)
  }
}

function _notifyNodesListeners(): void {
  _nodesVersion++
  _nodesVersionListeners.forEach(fn => fn())
}

/** Subscribe to org nodes map changes. Returns an unsubscribe function. */
export function subscribeToOrgNodes(cb: () => void): () => void {
  _nodesVersionListeners.add(cb)
  return () => _nodesVersionListeners.delete(cb)
}

/** Read a stable snapshot of the org nodes map. Memoized between version ticks. */
export function getStableOrgNodes(): Map<string, OrgNodeMeta> {
  if (_nodesVersion === _lastStableNodesVersion && _lastStableNodesSnapshot !== null) {
    return _lastStableNodesSnapshot
  }
  _lastStableNodesSnapshot = getOrgNodesSnapshot()
  _lastStableNodesVersion = _nodesVersion
  return _lastStableNodesSnapshot
}

/**
 * Read all nodes for the current dataspace as a snapshot Map.
 * Each entry contains { entityId, name, icon?, entityType }.
 *
 * Merges the REST fallback (if any, for the same dataspace) underneath the
 * live Y.Doc nodes map — the Y.Doc always wins per-key since it's the more
 * current source, but entities the Y.Doc hasn't (yet, or ever) synced still
 * resolve from REST instead of silently disappearing from every consumer.
 */
export function getOrgNodesSnapshot(): Map<string, OrgNodeMeta> {
  const result = new Map<string, OrgNodeMeta>()
  if (_restNodesFallback && _restNodesFallbackDataspaceId === _currentDataspaceId) {
    _restNodesFallback.forEach((meta, entityId) => result.set(entityId, meta))
  }
  if (!_ydoc) return result
  const nodesMap = _ydoc.getMap<string>('nodes')
  nodesMap.forEach((raw, entityId) => {
    try {
      const data = JSON.parse(raw)
      result.set(entityId, {
        entityId,
        name: data.name || '',
        icon: data.icon,
        entityType: data.entityType || '',
      })
    } catch { /* skip malformed */ }
  })
  return result
}

/**
 * Read all edges from the org Y.Doc as a snapshot.
 * Each entry's `edgeId` is the semantic key "(parentId or 'ROOT'):childId"
 * which equals getEdgeKey(childId, parentId) in dataspaceTreeStore.
 */
export function getOrgEdgesSnapshot(): Array<{
  edgeId: string
  parentId: string | null
  childId: string
  pos: string
  expanded: boolean
  kind: 'canonical' | 'alias'
}> {
  if (!_ydoc) return []
  const edgesMap = _ydoc.getMap<string>('edges')
  const result: ReturnType<typeof getOrgEdgesSnapshot> = []
  edgesMap.forEach((raw, edgeKey) => {
    try {
      const data = JSON.parse(raw)
      result.push({
        edgeId: edgeKey,  // semantic key — same format as nodesById keys
        parentId: data.parentId ?? null,
        childId: data.childId,
        pos: data.pos || 'M',
        expanded: data.expanded ?? false,
        kind: data.kind ?? 'canonical',
      })
    } catch { /* skip malformed */ }
  })
  return result
}

function _applyFullTreeSync(edgesMap: Y.Map<string>): Promise<void> {
  return import('./dataspaceTreeStore').then(({ useDataspaceTreeStore }) => {
    const store = useDataspaceTreeStore.getState()

    // If the tree hasn't been initialized yet, do a full replace from the Y.Doc.
    // If already initialized, merge — add/update edges without removing REST-sourced ones.
    if (!store.initialized) {
      const edges: Array<{
        id: string; dataspaceId: string; parentId: string | null; childId: string;
        pos: string; expanded: boolean; kind: 'canonical' | 'alias'; createdAt: string; updatedAt: string;
      }> = []

      edgesMap.forEach((raw, edgeKey) => {
        // edgeKey is the semantic key "(parentId or 'ROOT'):childId" after Phase 0a
        try {
          const data = JSON.parse(raw)
          edges.push({
            id: edgeKey,  // semantic key — used as nodesById key
            dataspaceId: _currentDataspaceId || '',
            parentId: data.parentId ?? null,
            childId: data.childId,
            pos: data.pos || 'M',
            expanded: data.expanded ?? false,
            kind: data.kind ?? 'canonical',
            createdAt: '',
            updatedAt: '',
          })
        } catch { /* skip malformed */ }
      })
      console.log(`[OrgYDoc] Full tree sync (init): ${edges.length} edges → initFromEdges`)
      store.initFromEdges(edges)
      return
    }

    // Merge: add or update edges from Y.Doc; don't remove edges not in Y.Doc.
    // edgeKey IS the nodesById key — no conversion needed.
    let merged = 0
    edgesMap.forEach((raw, edgeKey) => {
      try {
        const data = JSON.parse(raw)
        const existing = store.nodesById[edgeKey]
        if (!existing || existing.pos !== (data.pos || 'M') || existing.expanded !== (data.expanded ?? false)) {
          store._applyAddNode(edgeKey, {
            id: data.childId,
            parentId: data.parentId ?? null,
            pos: data.pos || 'M',
            expanded: data.expanded ?? false,
            kind: data.kind ?? 'canonical',
          })
          merged++
        }
      } catch { /* skip malformed */ }
    })
    if (merged > 0) {
      store._rebuildChildrenMap()
      console.log(`[OrgYDoc] Full tree sync (merge): ${merged} edges merged into existing tree`)
    }
  }).catch(err => {
    console.warn('[OrgYDoc] dataspaceTreeStore import failed:', err)
  })
}

function _applyFullNodeSync(nodesMap: Y.Map<string>): Promise<void> {
  // Walk the full nodes map and reconcile with whatever the registry already
  // holds. Only mutates entities the owning store already knows about — full
  // hydration of unknown entities flows through the per-space loader / global
  // store / app stores, not through this metadata sync.
  let updated = 0
  nodesMap.forEach((raw, entityId) => {
    try {
      const data = JSON.parse(raw)
      const existing = entityRegistry.getById(entityId)
      if (!existing) return
      const incomingName = data.name || existing.name
      const nameChanged = data.name && existing.name !== data.name
      const iconChanged = (existing as any).icon !== data.icon
      const existingSkeleton = (existing as any).skeleton
      const skeletonChanged = data.skeleton !== undefined && existingSkeleton !== undefined && (
        JSON.stringify(existingSkeleton) !== JSON.stringify({ ...existingSkeleton, ...data.skeleton })
      )
      if (nameChanged || iconChanged || skeletonChanged) {
        const merged: any = { ...existing, name: incomingName, icon: data.icon ?? (existing as any).icon }
        if (data.skeleton !== undefined) {
          merged.skeleton = { ...((existing as any).skeleton ?? {}), ...data.skeleton }
        }
        if (data.updated_at) merged.updated_at = data.updated_at
        entityRegistry.upsert(merged)
        updated++
      }
    } catch { /* skip */ }
  })
  if (updated > 0) {
    console.log(`[OrgYDoc] Full node sync: updated ${updated} entity names/skeletons`)
  }
  _notifyNodesListeners()
  return Promise.resolve()
}

// Pending edge changes accumulated across multiple Yjs observer callbacks in the same tick.
// Flushed once per macrotask via setTimeout(0) so back-to-back Yjs messages only trigger
// a single _applyEdgeBatch (and therefore a single _rebuildChildrenMap).
type EdgeNode = { id: string; parentId: string | null; pos: string; expanded: boolean; kind: 'canonical' | 'alias' }
const _pendingEdgeAdds = new Map<string, EdgeNode>()
const _pendingEdgeRemoves = new Set<string>()
let _edgeFlushScheduled = false

function _scheduleEdgeFlush(): void {
  if (_edgeFlushScheduled) return
  _edgeFlushScheduled = true
  setTimeout(() => {
    _edgeFlushScheduled = false
    if (_pendingEdgeAdds.size === 0 && _pendingEdgeRemoves.size === 0) return
    const adds = Array.from(_pendingEdgeAdds.entries()) as [string, EdgeNode][]
    const removes = Array.from(_pendingEdgeRemoves)
    _pendingEdgeAdds.clear()
    _pendingEdgeRemoves.clear()
    import('./dataspaceTreeStore').then(({ useDataspaceTreeStore }) => {
      useDataspaceTreeStore.getState()._applyEdgeBatch(adds, removes)
    }).catch(err => {
      console.warn('[OrgYDoc] dataspaceTreeStore import failed:', err)
    })
  }, 0)
}

function _handleEdgeChanges(event: Y.YMapEvent<string>, edgesMap: Y.Map<string>): void {
  // Capture changes synchronously — event.changes is invalidated once the observer returns.
  for (const [edgeKey, change] of event.changes.keys) {
    if (change.action === 'add' || change.action === 'update') {
      const raw = edgesMap.get(edgeKey)
      if (!raw) continue
      try {
        const data = JSON.parse(raw)
        const parentId = data.parentId ?? null
        if (parentId !== null && parentId === data.childId) {
          console.error('[OrgYDoc] _handleEdgeChanges: dropping self-parent edge', edgeKey)
          continue
        }
        _pendingEdgeAdds.set(edgeKey, {
          id: data.childId,
          parentId,
          pos: data.pos || 'M',
          expanded: data.expanded ?? false,
          kind: data.kind ?? 'canonical',
        })
        _pendingEdgeRemoves.delete(edgeKey) // add wins over remove if both arrive
      } catch { /* skip malformed */ }
    } else if (change.action === 'delete') {
      _pendingEdgeRemoves.add(edgeKey)
      _pendingEdgeAdds.delete(edgeKey)
    }
  }

  _scheduleEdgeFlush()
}

function _emitEntityEvent(type: 'created' | 'updated' | 'deleted', entity: any): void {
  import('../services/entityEventService').then(({ entityEventService }) => {
    // Use emitEntityEvent directly so we can set source: 'crdt'. Editors
    // (NotesEditor, PageEditor, etc.) and requestCacheService need to see
    // these CRDT-originating changes; per-action subscribers ignore 'local'
    // events because they were already processed by the originating action.
    entityEventService.emitEntityEvent({
      type,
      entity,
      spaceId: entity.spaceId || _currentDataspaceId || '',
      source: 'crdt',
    })
  }).catch(() => {})
}

// Pending entity IDs that arrived via CRDT 'add' but aren't in the entity store.
// Collected within the current setTimeout window and flushed as a single batch request.
const _pendingFetchIds: string[] = []
let _pendingFetchScheduled = false
let _pendingFetchTimer: ReturnType<typeof setTimeout> | null = null

const BATCH_FETCH_LIMIT = 200

async function _executeBatchFetch(ids: string[], scheduledForDataspaceId: string | null): Promise<void> {
  // Split into chunks of BATCH_FETCH_LIMIT to stay within URL length limits.
  // Hydrated entities are dispatched through entityRegistry.upsert which routes
  // each one to its owning store (space/global/app) by entityType.
  //
  // Cross-org contamination guard: if the active dataspace changes while this
  // batch is mid-flight (org switch), drop the result on the floor. Otherwise
  // the registry would dispatch entities from the old org into the new org's
  // global/space caches, which both makes the UI lie and corrupts IDB.
  for (let i = 0; i < ids.length; i += BATCH_FETCH_LIMIT) {
    const chunk = ids.slice(i, i + BATCH_FETCH_LIMIT)
    const idsParam = chunk.map(encodeURIComponent).join(',')
    try {
      const result: any = await fetchService(`${API_BASE_URL}/entities?ids=${idsParam}`, { credentials: 'include' })
      if (_currentDataspaceId !== scheduledForDataspaceId) {
        console.log(`[OrgYDoc] Batch fetch result discarded — dataspace switched from ...${(scheduledForDataspaceId || '').slice(-6)} to ...${(_currentDataspaceId || '').slice(-6)}`)
        return
      }
      const entities: any[] = Array.isArray(result) ? result : (result?.entities ?? [])
      for (const entity of entities) {
        if (!entity?._id) continue
        // Mail entities are owned by mailAppStore — route via the dedicated bridge.
        if (entity.entityType && MAIL_CRDT_TYPES.has(entity.entityType)) {
          import('./mailCrdtBridge').then(({ handleMailCrdtUpdate }) => handleMailCrdtUpdate(entity)).catch(() => {})
          _emitEntityEvent('created', entity)
          continue
        }
        entityRegistry.upsert(entity)
        _emitEntityEvent('created', entity)
      }
      console.log(`[OrgYDoc] Batch fetch complete: ${entities.length}/${chunk.length} entities dispatched via registry`)
    } catch (err) {
      console.warn(`[OrgYDoc] Batch fetch failed for ${chunk.length} entities:`, err)
    }
  }
}

function _scheduleBatchFetch(): void {
  if (_pendingFetchScheduled) return
  _pendingFetchScheduled = true
  // Use setTimeout instead of queueMicrotask: each WebSocket message arrives as a
  // separate event loop task, so queueMicrotask only collects IDs within a single
  // message. A 50ms window collapses all entity-add events from a burst of WS
  // messages into one batch request. The timer ID is stored so destroyOrgYDocStore
  // can cancel it and prevent stale cross-session fetches.
  _pendingFetchTimer = setTimeout(() => {
    _pendingFetchTimer = null
    const ids = _pendingFetchIds.splice(0)
    _pendingFetchScheduled = false
    if (ids.length === 0) return

    // Capture the dataspace at scheduling time so the post-await dispatch
    // can bail if the user switched orgs while the request was in flight.
    const scheduledFor = _currentDataspaceId
    log.debug(`[OrgYDoc] Batch-fetching ${ids.length} new entities from REST (dataspace=...${(scheduledFor || '').slice(-6)})`)
    _executeBatchFetch(ids, scheduledFor).catch(() => {})
  }, 50)
}

function _handleNodeChanges(event: Y.YMapEvent<string>): void {
  // Capture changes synchronously while still inside the YJS observer callback.
  // event.changes is invalidated once the observer returns, so accessing it
  // inside an async .then() throws "You must not compute changes after the
  // event-handler fired."
  type NodeChange = { entityId: string; action: 'add' | 'update' | 'delete'; raw: string | null }
  const nodesMap = _ydoc?.getMap<string>('nodes')
  const captured: NodeChange[] = []

  for (const [entityId, change] of event.changes.keys) {
    if (change.action === 'add' || change.action === 'update') {
      captured.push({ entityId, action: change.action, raw: nodesMap?.get(entityId) ?? null })
    } else if (change.action === 'delete') {
      captured.push({ entityId, action: 'delete', raw: null })
    }
  }

  if (captured.length === 0) return

  for (const { entityId, action, raw } of captured) {
    if (action === 'add' || action === 'update') {
      if (!raw) continue
      try {
        const data = JSON.parse(raw)

        // Mail entities are not in the global entity store — route via the bridge.
        if (data.entityType && MAIL_CRDT_TYPES.has(data.entityType)) {
          import('./mailCrdtBridge').then(({ handleMailCrdtUpdate }) => handleMailCrdtUpdate(data)).catch(() => {})
          _entityListeners.forEach(fn => fn(action === 'add' ? 'add' : 'update', entityId, data))
          continue
        }

        const existing = entityRegistry.getById(entityId)
        if (existing) {
          // Skeleton-only broadcasts (e.g. entity_update writing inboxProcessing)
          // send name:"" which must not overwrite the real entity name. Only count
          // a name change when the incoming name is non-empty.
          const incomingName = data.name || existing.name
          const nameChanged = data.name && existing.name !== data.name
          const iconChanged = (existing as any).icon !== data.icon
          const skeletonChanged = data.skeleton !== undefined && (
            JSON.stringify((existing as any).skeleton) !== JSON.stringify({ ...((existing as any).skeleton ?? {}), ...data.skeleton })
          )
          if (nameChanged || iconChanged || skeletonChanged) {
            const merged: any = { ...existing, name: incomingName, icon: data.icon ?? (existing as any).icon }
            if (data.skeleton !== undefined) {
              merged.skeleton = { ...((existing as any).skeleton ?? {}), ...data.skeleton }
            }
            // Propagate updated_at from the broadcast so downstream consumers
            // (automationsStore fingerprint, AutomationForm) see the correct
            // timestamp. Cold-load seeds don't include updated_at; only overwrite
            // when the broadcast explicitly carries it.
            if (data.updated_at) merged.updated_at = data.updated_at
            entityRegistry.upsert(merged)
            _emitEntityEvent('updated', merged)
          }
        } else if (action === 'add') {
          // First time we're seeing this entity — enqueue a batch fetch so we
          // hydrate the full record via REST and dispatch through the registry.
          // We restrict this to `add` events: an `update` for an entity not in
          // any local store usually means the owning store hasn't mounted yet
          // (e.g. opening a space that hasn't been visited this session). The
          // store will hydrate on its own; fetching here would just duplicate
          // that work and burn a network round-trip.
          _pendingFetchIds.push(entityId)
          _scheduleBatchFetch()
        }
        _entityListeners.forEach(fn => fn(action === 'add' ? 'add' : 'update', entityId, data))
      } catch { /* skip malformed entry */ }
    } else if (action === 'delete') {
      // Mail bridge handles its own types; for everything else dispatch the
      // tombstone through the registry. The deleted node's type is no longer
      // readable from the Y.Map, so we let the registry fan out to all scopes.
      import('./mailCrdtBridge').then(({ handleMailCrdtDelete }) => handleMailCrdtDelete(entityId)).catch(() => {})
      const deletedEntity = entityRegistry.getById(entityId)
      entityRegistry.deleteEntity(entityId)
      if (deletedEntity) _emitEntityEvent('deleted', deletedEntity)
      _entityListeners.forEach(fn => fn('delete', entityId))
    }
  }
  _notifyNodesListeners()
}
